'use client';

import { Typography } from '@musinsa/mds';
import type {
  GetSeedingMeRecruitmentServiceResponse,
  SeedingRecruitmentStatus,
} from '@musinsa/services-curator';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { curatorQueries } from '../../query-factory';
import { isSeedingContentDeadlineExpired } from '../utils/isSeedingContentDeadlineExpired';
import { SeedingContentExpiredGroupHeader } from './SeedingContentExpiredGroupHeader';
import { SeedingRecruitmentCard } from './SeedingRecruitmentCard';

const RECRUITMENT_EMPTY_TEXT: Record<SeedingRecruitmentStatus, string> = {
  PENDING: '신청한 캠페인이 없습니다.',
  CONFIRMED: '진행 중 캠페인이 없습니다.',
  COMPLETED: '완료된 캠페인이 없습니다.',
  NOT_SELECTED: '미선정 캠페인이 없습니다.',
};

interface Props {
  status: SeedingRecruitmentStatus;
}

const PAGE_SIZE = 20;

export const SeedingRecruitmentList = ({ status }: Props) => {
  const queryParams = useMemo(() => ({ searchStatus: status, size: PAGE_SIZE }), [status]);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    useInfiniteQuery({
      ...curatorQueries.campaigns.seedingMeRecruitment(queryParams),
      initialPageParam: 1,
      getNextPageParam: (lastPage: GetSeedingMeRecruitmentServiceResponse) =>
        lastPage.pagination.nextPage ?? undefined,
    });

  const items = useMemo(
    () => data?.pages.flatMap((page: GetSeedingMeRecruitmentServiceResponse) => page.list) ?? [],
    [data],
  );

  // 완료 탭 한정: 정상 완료 / 콘텐츠 제출 기한 경과(미제출) 2그룹 분할. 그 외 탭은 평면 렌더.
  // (QAT-39690 §3 — BE QAT-39765 가 기한경과 건을 완료 탭으로 보내며 participationStatus='CONFIRMED' 유지)
  const isCompletedTab = status === 'COMPLETED';
  const normalItems = useMemo(
    () => (isCompletedTab ? items.filter((it) => !isSeedingContentDeadlineExpired(it)) : items),
    [isCompletedTab, items],
  );
  const expiredItems = useMemo(
    () => (isCompletedTab ? items.filter(isSeedingContentDeadlineExpired) : []),
    [isCompletedTab, items],
  );

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: '600px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading && items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-[16px]! py-[40px]!">
        <Typography variant="body14pxReg" colorScheme="gray-500">
          불러오는 중…
        </Typography>
      </div>
    );
  }

  if (isError && items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-[16px]! py-[40px]!">
        <Typography variant="body14pxReg" colorScheme="gray-500">
          캠페인 목록을 불러오지 못했어요.
        </Typography>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-[12px]! px-[16px]! py-[40px]!">
        <div className="flex flex-col items-center gap-[2px]! w-full">
          <Typography size="14" weight="medium">
            {RECRUITMENT_EMPTY_TEXT[status]}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    // 하단 72px 여백 — PLP 하단 FAB/safe-area 클리어런스. (피드백 항목18)
    <div className="pt-[16px]! pb-[72px]!">
      {normalItems.map((item, index) => (
        <SeedingRecruitmentCard key={item.participationId} item={item} index={index} />
      ))}
      {expiredItems.length > 0 && <SeedingContentExpiredGroupHeader />}
      {expiredItems.map((item, index) => (
        <SeedingRecruitmentCard
          key={item.participationId}
          item={item}
          index={normalItems.length + index}
        />
      ))}
      <div ref={sentinelRef} className="h-[1px]" />
    </div>
  );
};
